import { Injectable } from '@angular/core';
import { ApiExpectedStrategy, ColClientAccountDetailOut, FrameworkContractOut, InvestmentGoals, DraftInitiator, NewPortfolioInput, NewClient, workplaceFlowSteps } from './rest-api/rest-api';
import { Location } from '@angular/common';
import { SelectItemTyped } from '../shared/dropdown/dropdown';
import { ExpectedOwnedStrategy } from '../shared/owned-strategy-card/strategy';
import { Router } from '@angular/router';
import { INVESTMENT_GOAL_ROUTE, WITHDRAWAL_FORM_ROUTE } from '../shared/constants';

export interface ClientServiceInput {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [index: string]: any,
  selectedClient?: ColClientAccountDetailOut,
  newClient?: NewClient | null,
  frameworkContract?: FrameworkContractOut,
  goal?: InvestmentGoals,
  newPortfolioForm?: NewPortfolioInput,
  newStrategyForm?: ApiExpectedStrategy,
  type?: 'portfolio' | 'strategy',
  comment?: string,
  zoojComment?: string,
  modelStrategyId?: number | 'own' | 'draft-template',
  draftInitiator: string,
  investmentDetail?: ExpectedOwnedStrategy,
  feeConfigId?: number,
  // unsent values of investment draft steps, outside of sorted steps so back button keeps them
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  strategyFormCache?: Record<string, any>,
  strategyWeightsDistributed?: boolean,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  portfolioFormCache?: Record<string, any>,
  commentCache?: string,
  zoojCommentCache?: string,
  // new regular draft
  draftForm?: boolean,
  withdrawalComment?: boolean,
  withdrawalFinalCheck?: boolean,
  routeFromClientList?: boolean,
}

export type DraftType = 'regular-withdrawal' | 'investment';

const workplaceStepsSorted: string[][] = [
  ['client-picker'],
  ['clientsList'],
  ['selectedClient'],
  ['frameworkContract'],
  ['goal'],
  [workplaceFlowSteps.modelStrategyId, 'type'],
  ['newPortfolioForm', 'newStrategyForm'],
  ['comment'],
  ['finalCheck'],
  ['investmentDetail'],
];

const newClientStepsSorted: string[][] = [
  ['client-picker'],
  ['newClient'],
  [workplaceFlowSteps.modelStrategyId, 'type'],
  ['newPortfolioForm', 'newStrategyForm'],
  ['comment'],
  ['finalCheck'],
  ['investmentDetail'],
];

const regularDepositStepsSorted: string[][] = [
  ['draftForm'],
  ['withdrawalComment'],
  ['withdrawalFinalCheck'],
];

@Injectable({
  providedIn: 'root'
})
export class ConsultClientService {
  private clientStepsData: ClientServiceInput = {
    draftInitiator: DraftInitiator.COLLABORATOR,
  };

  private draftType: DraftType | null = null;

  public constructor(
    private location: Location,
    private router: Router,
  ) {}

  public draftInitiatorList: SelectItemTyped<DraftInitiator>[] = [
    { label: 'consultClient.draftInitiator.COLLABORATOR', value: DraftInitiator.COLLABORATOR, translate: true },
    { label: 'consultClient.draftInitiator.CLIENT', value: DraftInitiator.CLIENT, translate: true }
  ];

  public setStep<K extends keyof ClientServiceInput>(key: K, value: ClientServiceInput[K]): void {
    this.clientStepsData[key] = value;
  }

  public getAllSteps(): ClientServiceInput {
    return this.clientStepsData;
  }

  public getStep<K extends keyof ClientServiceInput>(key: K): NonNullable<ClientServiceInput[K]> {
    const keyValue = this.clientStepsData[key];
    if(!keyValue) {
      // missing step data and redirect to first step should be handled by guard
      throw new Error(`Missing data for step ${key}`);
    }
    return this.clientStepsData[key];
  }

  public clearDataAndSetDefault(): void {
    this.clientStepsData = {
      draftInitiator: DraftInitiator.COLLABORATOR,
    };
  }

  // keeps client, contract, goal and type, removes everything entered from strategy pick onwards
  public clearInvestmentDraft(): void {
    const draftSteps = [
      workplaceFlowSteps.modelStrategyId, 'feeConfigId', 'newStrategyForm', 'newPortfolioForm', 'comment', 'zoojComment',
      'strategyFormCache', 'strategyWeightsDistributed', 'portfolioFormCache', 'commentCache', 'zoojCommentCache',
    ];
    for (const step of draftSteps) {
      this.removeStep(step);
    }
    this.clientStepsData.draftInitiator = DraftInitiator.COLLABORATOR;
  }

  public goToPrevStep(): void {
    const actualStep = this.getActualStep();
    if(!this.router.url.includes(WITHDRAWAL_FORM_ROUTE) && !this.router.url.includes(INVESTMENT_GOAL_ROUTE) ) {
      this.removeStep(actualStep);
    }
    this.location.back();
  }

  public removeStep<K extends keyof ClientServiceInput>(key: K): void {
    delete this.clientStepsData[key];
  }

  private getActualStep(): string {
    // reverse so first returned = last reached step
    if(this.clientStepsData.newClient) {
      for (const steps of [...newClientStepsSorted].reverse()) {
        for (const nestedStep of steps) {
          if(this.clientStepsData[nestedStep]) {
            return nestedStep;
          }
        }
      }
    } else if(this.clientStepsData.draftForm) {
      for (const steps of [...regularDepositStepsSorted].reverse()) {
        for (const nestedStep of steps) {
          if(this.clientStepsData[nestedStep]) {
            return nestedStep;
          }
        }
      }
    } else {
      for (const steps of [...workplaceStepsSorted].reverse()) {
        for (const nestedStep of steps) {
          if(this.clientStepsData[nestedStep]) {
            return nestedStep;
          }
        }
      }
    }
    // loop should always find step
    return '';
  }

  public clearNewClient(): void {
    this.clientStepsData['newClient'] = null;
  }

  public setDraftType(type: DraftType): void {
    this.draftType = type;
  }

  public resetDraftType(): void {
    this.draftType = null;
  }

  public getDraftType(): DraftType | null {
    return this.draftType;
  }
}
